'use client';

import { useBunja } from 'bunja/react';
import { useAtom, useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { type FormEvent } from 'react';

import { T } from '@flipster/i18n';
import { Button } from '@flipster/react/design-system/button';
import CautionCircleIcon from '@flipster/react/icon/generated/CautionCircleIcon';
import SearchIcon from '@flipster/react/icon/generated/SearchIcon';
import { css } from '@flipster/styled-system/css';
import { hstack, stack, vstack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';

import { ChannelDropdown } from '../../components/channel-dropdown';
import { channelHintKeyMap, channelPlaceholderMap } from '../../constants';
import { channelVerificationPageBunja } from '../../state';
import Shield from './assets/shield.svg';

export const HeroSection = () => {
  const t = useTranslations();
  const { formAtom, isVerifyingAtom, verify } = useBunja(channelVerificationPageBunja);
  const [form, setForm] = useAtom(formAtom);
  const { channel, searchValue } = form;
  const isVerifying = useAtomValue(isVerifyingAtom);

  return (
    <div
      className={css({
        tabletDown: vstack.raw({ gap: 24, flexDirection: 'column-reverse' }),
        tablet: hstack.raw({ justifyContent: 'space-between' }),
      })}
    >
      <div className={stack({ direction: 'column', gap: 40, alignItems: 'flex-start', tabletDown: { w: '100%' } })}>
        <div className={stack({ gap: 16, tabletDown: { textAlign: 'center' } })}>
          <h1 className={css({ textStyle: 'Display/XL', color: 'content.default.level-5' })}>
            <T id='VIP.r260316.Official_channel_verification' />
          </h1>
          <p className={css({ textStyle: 'Title/XS', color: 'content.default.level-3' })}>
            <T id='VIP.r260316.Verify_whether_a_social_account_email_or_website_is_an_official_flipster_channel' />
          </p>
        </div>
        <form
          onSubmit={async (e: FormEvent) => (e.preventDefault(), await verify())}
          className={stack({ gap: 12, w: '100%' })}
        >
          <div className={css({ gap: 10, tabletDown: stack.raw(), tablet: hstack.raw() })}>
            <ChannelDropdown
              selected={channel}
              onChange={(channel) => setForm({ channel })}
              css={{ minW: 160, py: 12, pl: 20, pr: 12, tabletDown: { w: '100%' } }}
            />
            <div
              className={hstack({
                flex: 1,
                py: 13,
                px: 12,
                gap: 8,
                borderColor: 'border.default.level-3',
                borderWidth: '1px',
                borderStyle: 'solid',
                rounded: 8,
                tablet: {
                  minW: 445,
                },
              })}
            >
              <SearchIcon size={20} color={token('colors.content.default.level-2')} />
              <input
                type='text'
                value={searchValue}
                onChange={(e) => setForm({ searchValue: e.currentTarget.value })}
                placeholder={channelPlaceholderMap[channel]}
                className={css({
                  flex: 1,
                  textStyle: 'Text/L/Regular',
                  color: 'content.default.level-5',
                  _placeholder: { color: 'content.default.level-1' },
                })}
              />
            </div>
            <Button
              hierarchy='primary'
              color='accent'
              size='large'
              label={t('VIP.r260316.Search_button')}
              htmlType='submit'
              isLoading={isVerifying}
              withSpinner
            />
          </div>
          <div className={hstack({ gap: 8 })}>
            <CautionCircleIcon
              size={16}
              color={token('colors.content.default.level-3')}
              className={css({ flexShrink: 0 })}
            />
            <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>
              <T id={channelHintKeyMap[channel]} />
            </p>
          </div>
        </form>
      </div>
      <Shield className={css({ tabletDown: { w: 200, h: 200 } })} />
    </div>
  );
};
